import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge, Accordion } from 'react-bootstrap';
import { FaSave, FaSync, FaCheckCircle, FaCog, FaEnvelope, FaCreditCard, FaStar, FaChartBar } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import settingsService from '../services/settingsService';

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState({});
  const [changedSettings, setChangedSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await settingsService.getCategories();
      setCategories(data.categories || {});
      
      // If no settings found, try to initialize defaults
      if (Object.keys(data.categories || {}).length === 0) {
        try {
          await settingsService.initializeDefaults();
          const retryData = await settingsService.getCategories();
          setCategories(retryData.categories || {});
        } catch (initError) {
          console.error('Failed to initialize defaults:', initError);
        }
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      if (err.response?.status === 403) {
        setError('Admin access required. Please log in as an administrator.');
      } else if (err.response?.status === 401) {
        setError('Authentication required. Please log in first.');
      } else {
        setError('Failed to load settings. ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value, dataType) => {
    // Store the changed value
    setChangedSettings(prev => ({
      ...prev,
      [key]: { value, dataType }
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Prepare settings object for bulk update
      const settingsToUpdate = {};
      Object.entries(changedSettings).forEach(([key, data]) => {
        settingsToUpdate[key] = data.value;
      });

      await settingsService.bulkUpdate(settingsToUpdate);
      
      setSuccess('Settings saved successfully!');
      setChangedSettings({});
      
      // Refresh settings
      await fetchSettings();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('Initialize default settings? This will create missing default settings but won\'t overwrite existing ones.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await settingsService.initializeDefaults();
      setSuccess('Default settings initialized successfully!');
      await fetchSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to initialize defaults:', err);
      setError('Failed to initialize default settings.');
    } finally {
      setLoading(false);
    }
  };

  const renderSettingControl = (setting) => {
    const currentValue = changedSettings[setting.key]?.value ?? setting.value;

    if (setting.data_type === 'boolean') {
      return (
        <Form.Check
          type="switch"
          id={`setting-${setting.key}`}
          label={currentValue ? 'Enabled' : 'Disabled'}
          checked={currentValue}
          onChange={(e) => handleSettingChange(setting.key, e.target.checked, 'boolean')}
        />
      );
    } else if (setting.data_type === 'integer') {
      return (
        <Form.Control
          type="number"
          value={currentValue}
          onChange={(e) => handleSettingChange(setting.key, parseInt(e.target.value) || 0, 'integer')}
        />
      );
    } else {
      return (
        <Form.Control
          type="text"
          value={currentValue}
          onChange={(e) => handleSettingChange(setting.key, e.target.value, 'string')}
        />
      );
    }
  };

  const getCategoryIcon = (category) => {
    const iconProps = { size: 18, className: 'text-primary' };
    switch (category) {
      case 'features':
        return <FaStar {...iconProps} />;
      case 'payment':
        return <FaCreditCard {...iconProps} />;
      case 'analytics':
        return <FaChartBar {...iconProps} />;
      case 'email':
        return <FaEnvelope {...iconProps} />;
      case 'general':
        return <FaCog {...iconProps} />;
      default:
        return <FaCog {...iconProps} />;
    }
  };

  const getCategoryTitle = (category) => {
    return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');
  };

  if (loading) {
    return (
      <Container fluid className="px-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  const hasChanges = Object.keys(changedSettings).length > 0;

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Settings"
        subtitle="Manage application features and configuration"
      />

      <Row className="mb-3">
        <Col>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              <FaCheckCircle className="me-2" />
              {success}
            </Alert>
          )}
        </Col>
      </Row>

      {Object.keys(categories).length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <h5 className="text-muted mb-3">No Settings Found</h5>
            <p className="text-muted mb-4">Initialize default settings to get started.</p>
            <Button variant="primary" onClick={handleInitializeDefaults}>
              <FaSync className="me-2" />
              Initialize Default Settings
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div>
                {hasChanges && (
                  <Badge bg="warning" className="me-2">
                    {Object.keys(changedSettings).length} unsaved change(s)
                  </Badge>
                )}
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-2"
                  onClick={handleInitializeDefaults}
                >
                  <FaSync className="me-1" />
                  Initialize Defaults
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveSettings}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>

          <Accordion defaultActiveKey="0">
            {['general', 'email', 'payment', 'features', 'analytics']
              .filter(categoryName => categories[categoryName])
              .map((categoryName, index) => {
                const settings = categories[categoryName];
                return (
              <Accordion.Item eventKey={index.toString()} key={categoryName}>
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <span className="me-2">{getCategoryIcon(categoryName)}</span>
                    <strong>{getCategoryTitle(categoryName)}</strong>
                    <Badge bg="secondary" className="ms-2">{settings.length}</Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className="g-3">
                    {settings.map((setting) => (
                      <Col md={6} key={setting.key}>
                        <Card className="border h-100">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <h6 className="mb-1">
                                  {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </h6>
                                {setting.description && (
                                  <small className="text-muted">{setting.description}</small>
                                )}
                              </div>
                              <div className="ms-2">
                                {setting.is_public ? (
                                  <Badge bg="primary" pill>Public</Badge>
                                ) : (
                                  <Badge bg="secondary" pill>Private</Badge>
                                )}
                              </div>
                            </div>
                            <div className="mt-3">
                              {renderSettingControl(setting)}
                            </div>
                            {changedSettings[setting.key] && (
                              <div className="mt-2">
                                <Badge bg="warning">Modified</Badge>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            );
            })}
          </Accordion>
        </>
      )}
    </Container>
  );
}

export default SettingsPage;
